import React from 'react';
import { useNavigate } from 'react-router-dom';

const VoiceHelpPage: React.FC = () => {
  const navigate = useNavigate();

  const quickActions = [
    { text: "Show me the menu", icon: "📋" },
    { text: "I want a pizza", icon: "🍕" },
    { text: "Add a burger to my order", icon: "🍔" },
    { text: "What's in my cart?", icon: "🛒" },
    { text: "Confirm my order", icon: "✅" },
    { text: "Make it spicy", icon: "🌶️" },
  ];

  const voiceCommands = [
    { text: "I want a pizza", icon: "🍕" },
    { text: "Show me the menu", icon: "📋" },
    { text: "Book a table for 4", icon: "📅" },
    { text: "I'm ready to order", icon: "✅" },
  ];

  return (
    <div className="min-h-screen py-8" style={{ backgroundColor: 'var(--color-background)' }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <button
            onClick={() => navigate(-1)}
            className="mb-4 flex items-center gap-2 mx-auto hover:opacity-70 transition-opacity"
            style={{ color: 'var(--color-primary)' }}
          >
            <span>←</span> Back to Voice Ordering
          </button>
          <h1
            className="text-4xl font-bold mb-4"
            style={{ color: 'var(--color-text-primary)' }}
          >
            🎤 Voice Ordering Help
          </h1>
          <p
            className="text-xl"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            Learn how to use voice commands effectively
          </p>
        </div>

        <div className="space-y-8">
          {/* Voice Commands */}
          <div
            className="rounded-xl shadow-lg p-6 border"
            style={{
              backgroundColor: 'var(--color-background)',
              borderColor: 'var(--color-surface)'
            }}
          >
            <h2
              className="text-2xl font-bold mb-4 flex items-center gap-2"
              style={{ color: 'var(--color-text-primary)' }}
            >
              💡 Voice Commands
            </h2>
            <p
              className="mb-6"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              Use these common voice commands to interact with our AI assistant:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {voiceCommands.map((command, index) => (
                <div
                  key={index}
                  className="p-4 rounded-lg border shadow-sm"
                  style={{
                    backgroundColor: 'var(--color-surface)',
                    borderColor: 'var(--color-primary)'
                  }}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{command.icon}</span>
                    <p
                      className="font-medium"
                      style={{ color: 'var(--color-text-primary)' }}
                    >
                      "{command.text}"
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Commands */}
          <div
            className="rounded-xl shadow-lg p-6 border"
            style={{
              backgroundColor: 'var(--color-background)',
              borderColor: 'var(--color-surface)'
            }}
          >
            <h2
              className="text-2xl font-bold mb-4"
              style={{ color: 'var(--color-text-primary)' }}
            >
              ⚡ Quick Commands
            </h2>
            <p
              className="mb-6"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              Try these quick commands to speed up your ordering:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {quickActions.map((action, index) => (
                <div
                  key={index}
                  className="flex items-center space-x-3 p-4 rounded-lg transition-all border hover:opacity-80"
                  style={{
                    backgroundColor: 'var(--color-surface)',
                    borderColor: 'var(--color-primary)'
                  }}
                >
                  <span className="text-3xl">{action.icon}</span>
                  <span
                    className="font-medium"
                    style={{ color: 'var(--color-text-primary)' }}
                  >
                    "{action.text}"
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Popular Items for Voice Reference */}
          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              🌟 Popular Items - Try Saying These Names
            </h2>
            <p className="text-gray-600 mb-6">
              Here are some popular items you can order by name:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <span className="text-blue-600 font-bold text-xl">🍕</span>
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900">Margherita Pizza</h4>
                  <p className="text-sm text-gray-600">£12.99</p>
                  <p className="text-xs text-blue-600">Say: "I want a margherita pizza"</p>
                </div>
              </div>
              <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <span className="text-green-600 font-bold text-xl">🍔</span>
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900">Classic Burger</h4>
                  <p className="text-sm text-gray-600">£9.99</p>
                  <p className="text-xs text-blue-600">Say: "Add a burger"</p>
                </div>
              </div>
              <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                  <span className="text-orange-600 font-bold text-xl">🍝</span>
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900">Pasta Carbonara</h4>
                  <p className="text-sm text-gray-600">£11.99</p>
                  <p className="text-xs text-blue-600">Say: "I'll have the carbonara"</p>
                </div>
              </div>
              <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                  <span className="text-red-600 font-bold text-xl">🥗</span>
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900">Caesar Salad</h4>
                  <p className="text-sm text-gray-600">£7.99</p>
                  <p className="text-xs text-blue-600">Say: "Give me a caesar salad"</p>
                </div>
              </div>
            </div>
          </div>

          {/* Voice Tips */}
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl shadow-lg p-6 border-2 border-green-200">
            <h2 className="text-2xl font-bold text-green-900 mb-4">
              💡 Voice Tips
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <span className="text-green-600 text-xl font-bold">•</span>
                  <div>
                    <h4 className="font-semibold text-green-900 mb-1">Speak Clearly</h4>
                    <p className="text-sm text-green-800">
                      Speak naturally and clearly for best recognition
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="text-green-600 text-xl font-bold">•</span>
                  <div>
                    <h4 className="font-semibold text-green-900 mb-1">Use Action Words</h4>
                    <p className="text-sm text-green-800">
                      Say "Add" or "I want" to order items
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="text-green-600 text-xl font-bold">•</span>
                  <div>
                    <h4 className="font-semibold text-green-900 mb-1">Mention Quantity</h4>
                    <p className="text-sm text-green-800">
                      Say "Two pizzas" or "Three burgers"
                    </p>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <span className="text-green-600 text-xl font-bold">•</span>
                  <div>
                    <h4 className="font-semibold text-green-900 mb-1">Specify Modifications</h4>
                    <p className="text-sm text-green-800">
                      Add details like "Extra cheese" or "No onions"
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="text-green-600 text-xl font-bold">•</span>
                  <div>
                    <h4 className="font-semibold text-green-900 mb-1">Confirm When Ready</h4>
                    <p className="text-sm text-green-800">
                      Say "Confirm" or "I'm done" when ready to order
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="text-green-600 text-xl font-bold">•</span>
                  <div>
                    <h4 className="font-semibold text-green-900 mb-1">Ask for Help</h4>
                    <p className="text-sm text-green-800">
                      Say "Help" or "What can I order?" anytime
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Example Conversation */}
          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              💬 Example Conversation
            </h2>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                  You
                </div>
                <div className="bg-blue-50 rounded-lg p-3 flex-1 border border-blue-200">
                  <p className="text-gray-800">"Hi, I'd like to order a pizza"</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                  AI
                </div>
                <div className="bg-purple-50 rounded-lg p-3 flex-1 border border-purple-200">
                  <p className="text-gray-800">"Great! What type of pizza would you like?"</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                  You
                </div>
                <div className="bg-blue-50 rounded-lg p-3 flex-1 border border-blue-200">
                  <p className="text-gray-800">"Margherita pizza, please"</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                  AI
                </div>
                <div className="bg-purple-50 rounded-lg p-3 flex-1 border border-purple-200">
                  <p className="text-gray-800">"Perfect! I've added a Margherita Pizza. Would you like any drinks?"</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                  You
                </div>
                <div className="bg-blue-50 rounded-lg p-3 flex-1 border border-blue-200">
                  <p className="text-gray-800">"Yes, add a coke please"</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                  AI
                </div>
                <div className="bg-purple-50 rounded-lg p-3 flex-1 border border-purple-200">
                  <p className="text-gray-800">"Done! Anything else?"</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                  You
                </div>
                <div className="bg-blue-50 rounded-lg p-3 flex-1 border border-blue-200">
                  <p className="text-gray-800">"No, that's all. Confirm my order"</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Back Button */}
        <div className="text-center mt-8">
          <button
            onClick={() => navigate(-1)}
            className="text-white font-semibold px-8 py-3 rounded-lg shadow-lg transition-all transform hover:scale-105"
            style={{
              background: `linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-dark) 100%)`
            }}
          >
            Back to Voice Ordering
          </button>
        </div>
      </div>
    </div>
  );
};

export default VoiceHelpPage;
